'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib components
import {
  Box,
  Divider,
  DividerProps,
  Group,
  Button,
  Text,
  Flex,
} from '@mantine/core';

// UI local components
import SearchSuggestionsSection from '@features/Search/SearchBar/SearchSuggestionsSection';

// Types
import { SearchSuggestionsProps } from './types';

// Assets
import { IconExternalLink as IconLink } from '@tabler/icons-react';

// Style
import * as classes from './styles/index.css';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { palette } from '@app/shared/ui-kit/colors';
import MatchQuestionItem from '../MatchQuestionItem';
import MatchResourceItem from '../MatchResourceItem';

// Constants
const DIVIDER_PROPS: DividerProps = {
  size: 'xs',
  c: palette.blackroomLightGray3,
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
function SearchSuggestions({
  searchResult,
  query = '',
  onGoToSearchResults,
  onClickResult,
}: SearchSuggestionsProps) {
  /* ********************************** HOOKS ********************************* */

  const hasResults =
    !!searchResult?.resources.items.length ||
    !!searchResult?.questions.items.length;

  // Localization
  const { t } = useTranslation('Search');

  // Style

  /* ******************************** RENDERING ******************************* */

  if (!searchResult) {
    return null;
  }

  return (
    <Box>
      {hasResults ? (
        <>
          <Flex direction="column" gap={10}>
            {searchResult.resources.items.length > 0 && (
              <SearchSuggestionsSection
                title={t('SearchFilters.filters.resource')}
                totalCount={searchResult.resources.totalCount}
              >
                {searchResult.resources.items.map((resource) => (
                  <MatchResourceItem
                    key={resource.id}
                    query={query}
                    resource={{
                      id: resource.id,
                      index: resource.index,
                      name: resource.name,
                      type: resource.type,
                      mimeType: resource.mimeType,
                    }}
                    onClick={onClickResult}
                  />
                ))}
              </SearchSuggestionsSection>
            )}

            {searchResult.questions.items.length > 0 && (
              <SearchSuggestionsSection
                title={t('SearchFilters.filters.question')}
                totalCount={searchResult.questions.totalCount}
              >
                {searchResult.questions.items.map((question) => (
                  <MatchQuestionItem
                    key={question.id}
                    query={query}
                    question={{ id: question.id, title: question.title }}
                    onClick={onClickResult}
                  />
                ))}
              </SearchSuggestionsSection>
            )}
          </Flex>

          <Divider {...DIVIDER_PROPS} my={5} />

          {/* // @TODO: Uncomment here if you want to support group search */}
          {/* {searchResult.groups.items.length > 0 && (
          <>
            <SearchSuggestionsSection
              title={t('SearchFilters.filters.group')}
              totalCount={searchResult.groups.totalCount}
            >
              {searchResult.groups.items.map((group) => (
                <MatchGroupItem
                  key={group.id}
                  query={query}
                  group={{ name: group.name, color: group.color }}
                />
              ))}
            </SearchSuggestionsSection>

            <Divider {...DIVIDER_PROPS} />
          </>
        )} */}

          {/* // @TODO: Uncomment here if you want to support user search
        // Disabled as requested by Loic Moisand */}
          {/* {searchResult.users.items.length > 0 && (
          <SearchSuggestionsSection
            title={t('SearchFilters.filters.user')}
            totalCount={searchResult.users.totalCount}
          >
            {searchResult.users.items.map((user) => (
              <MatchUserItem
                key={user.id}
                query={query}
                user={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  pictureURL: user.pictureURL,
                }}
              />
            ))}
          </SearchSuggestionsSection>
        )} */}

          <Button
            variant="subtle"
            h={40}
            mb={5}
            px="sm"
            fullWidth
            classNames={{
              inner: classes.goToSearchPageButtonInner,
              root: classes.goToSearchPageButtonRoot,
            }}
            onClick={() => onGoToSearchResults?.()}
          >
            <Group gap={8}>
              <IconLink size={20} />
              <Text c="blackroomBlue" fz="sm" fw={FONT_WEIGHT.bold}>
                {t('SearchSuggestions.goToPageSearch')}
              </Text>
            </Group>
          </Button>
        </>
      ) : (
        <Text px="sm" pt={7} pb="xs" fz="sm" c={palette.blackroomLightGray5}>
          {t('PageSearch.empty.title')}
        </Text>
      )}
    </Box>
  );
}

export default SearchSuggestions;
